import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDownIcon, LayersIcon, RotateCcwIcon, SearchIcon, UsersIcon, ZapIcon } from 'lucide-react';
import { LEAD_SOURCES, LOAN_OFFICERS, type LeadSourceId, type LoanOfficer } from '../data/leadDistribution';
import type { DailyTimeBlock } from '../utils/timeBlocks';
import { isScheduledNow } from '../utils/timeBlocks';
import { LoanOfficerRow } from './LoanOfficerRow';
import { StateLicensesModal } from './StateLicensesModal';
interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}
function StatCard({
  icon,
  label,
  value,
  sub
}: StatCardProps) {
  return <div className="flex items-center gap-3 rounded-lg border border-slate-200 border-t-2 border-t-rcm-500 bg-white px-4 py-3.5 shadow-sm" data-id="element-9" data-mp="1mgf5pqigf6jh">
      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-rcm-50 text-rcm-600" data-id="element-10" data-mp="azp2mg9buuu3">
        {icon}
      </div>
      <div data-id="element-11" data-mp="5yrdtkttb2jm">
        <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400" data-id="element-12" data-mp="62f6khrfnkxa">
          {label}
        </div>
        <div className="flex items-baseline gap-1.5" data-id="element-13" data-mp="2pqdpnwqzrjl5">
          <span className="text-xl font-bold text-slate-800 tabular-nums" data-id="element-14" data-mp="2ppu84h596x7n">{value}</span>
          {sub && <span className="text-xs text-slate-400" data-id="element-15" data-mp="2bpt813wtbtt8">{sub}</span>}
        </div>
      </div>
    </div>;
}
export function LeadDistributionBoard() {
  const [now, setNow] = useState(() => new Date());
  const [officers, setOfficers] = useState<LoanOfficer[]>(() => syncOfficerReadiness(LOAN_OFFICERS, new Date()));
  const [showNotReady, setShowNotReady] = useState(true);
  const [query, setQuery] = useState('');
  const [licensesOfficerId, setLicensesOfficerId] = useState<string | null>(null);
  const licensesOfficer = officers.find(officer => officer.id === licensesOfficerId) ?? null;
  useEffect(() => {
    const syncSchedule = () => {
      const nextNow = new Date();
      setNow(nextNow);
      setOfficers(previous => syncOfficerReadiness(previous, nextNow));
    };
    const interval = window.setInterval(syncSchedule, 30_000);
    return () => window.clearInterval(interval);
  }, []);
  const refreshSchedules = () => {
    const nextNow = new Date();
    setNow(nextNow);
    setOfficers(previous => syncOfficerReadiness(previous, nextNow));
  };
  const updateTimeBlocks = (id: string, timeBlocks: DailyTimeBlock[]) => {
    setOfficers(previous => previous.map(officer => officer.id === id ? {
      ...officer,
      timeBlocks,
      manualReady: undefined,
      ready: isScheduledNow(timeBlocks, now)
    } : officer));
  };
  const toggleReady = (id: string) => {
    setOfficers(previous => previous.map(officer => officer.id === id ? {
      ...officer,
      manualReady: !officer.ready,
      ready: !officer.ready
    } : officer));
  };
  const addSource = (id: string, sourceId: LeadSourceId) => {
    setOfficers(previous => previous.map(officer => {
      if (officer.id !== id) return officer;
      const existing = officer.capacities.find(capacity => capacity.sourceId === sourceId);
      const defaultCap = sourceId === 'bankrate' || sourceId === 'bankingbridge' ? 3 : 5;
      return {
        ...officer,
        capacities: existing ? officer.capacities.map(capacity => capacity.sourceId === sourceId ? {
          ...capacity,
          eligible: true
        } : capacity) : [...officer.capacities, {
          sourceId,
          assigned: 0,
          cap: defaultCap,
          eligible: true
        }]
      };
    }));
  };
  const adjustCap = (id: string, sourceId: LeadSourceId, delta: number) => {
    setOfficers(previous => previous.map(officer => officer.id === id ? {
      ...officer,
      capacities: officer.capacities.map(capacity => capacity.sourceId === sourceId ? {
        ...capacity,
        cap: Math.max(capacity.assigned, capacity.cap + delta)
      } : capacity)
    } : officer));
  };
  const filtered = useMemo(() => officers.filter(officer => showNotReady || officer.ready).filter(officer => {
    const term = query.trim().toLowerCase();
    return !term || officer.name.toLowerCase().includes(term) || officer.email.toLowerCase().includes(term);
  }), [officers, query, showNotReady]);
  const sorted = useMemo(() => [...filtered].sort((first, second) => {
    if (first.ready !== second.ready) return first.ready ? -1 : 1;
    const firstTime = first.lastAssignedAt ? new Date(first.lastAssignedAt).getTime() : 0;
    const secondTime = second.lastAssignedAt ? new Date(second.lastAssignedAt).getTime() : 0;
    return firstTime - secondTime;
  }), [filtered]);
  const readyOfficers = officers.filter(officer => officer.ready);
  const readyCount = readyOfficers.length;
  const notReadyCount = officers.length - readyCount;
  const nextUpId = useMemo(() => {
    const candidate = [...readyOfficers].filter(officer => officer.capacities.some(capacity => capacity.eligible && capacity.cap > capacity.assigned)).sort((first, second) => {
      const firstTime = first.lastAssignedAt ? new Date(first.lastAssignedAt).getTime() : 0;
      const secondTime = second.lastAssignedAt ? new Date(second.lastAssignedAt).getTime() : 0;
      return firstTime - secondTime;
    })[0];
    return candidate?.id ?? null;
  }, [readyOfficers]);
  const totalRemaining = readyOfficers.reduce((total, officer) => total + officer.capacities.reduce((sum, capacity) => capacity.eligible ? sum + Math.max(capacity.cap - capacity.assigned, 0) : sum, 0), 0);
  return <div className="min-h-full w-full bg-[#f4f7fa] text-slate-700" data-id="element-16" data-mp="ps4wtp61bb72">
      <main className="mx-auto w-full max-w-[1920px] px-5 py-6 sm:px-8 sm:py-8 xl:px-10" data-id="element-17" data-mp="35fv5kkjm5lz4">
        <div className="mb-6 flex flex-col justify-between gap-3 sm:flex-row sm:items-end" data-id="element-18" data-mp="3ahc62ey6r6f6">
          <div data-id="element-19" data-mp="2qdd9de03ejnj">
            <h1 className="text-xl font-bold text-slate-800" data-id="element-20" data-mp="2qdwt909azi47">Today’s assignment queue</h1>
            <p className="mt-1 text-sm text-slate-500" data-id="element-21" data-mp="2me4do3lhsbpc">Set weekly availability windows; ready status updates automatically when a block opens or closes.</p>
          </div>
          <div className="flex items-center gap-3" data-id="element-22" data-mp="2cnjlaxwew3ev">
            <p className="text-xs font-medium text-slate-400" data-id="element-23" data-mp="2ckxdqy9itnwn">Thursday, May 14 · Local time</p>
            <button type="button" onClick={refreshSchedules} className="inline-flex items-center gap-1.5 rounded-md bg-rcm-500 px-3.5 py-2 text-xs font-bold text-white shadow-sm transition-colors hover:bg-rcm-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-rcm-500 focus-visible:ring-offset-2" data-id="element-24" data-mp="27p8bgn649swn">
              <RotateCcwIcon className="h-3.5 w-3.5" data-id="element-25" data-mp="3t08fi3i94b8d" />
              Refresh schedules
            </button>
          </div>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3" data-id="element-26" data-mp="3o03vw4m1r0dx">
          <StatCard icon={<ZapIcon className="h-5 w-5" data-id="element-28" data-mp="3vl6ar9tfjd9e" />} label="Ready now" value={String(readyCount)} sub={`of ${officers.length} LOs`} data-id="element-27" data-mp="3vkwljog7la4r" />
          <StatCard icon={<LayersIcon className="h-5 w-5" data-id="element-30" data-mp="964y6ay8f9sd" />} label="Capacity remaining" value={String(totalRemaining)} sub="leads today" data-id="element-29" data-mp="4d1xzjyy9237" />
          <StatCard icon={<UsersIcon className="h-5 w-5" data-id="element-32" data-mp="e3wlernxm7q8" />} label="Not ready" value={String(notReadyCount)} sub="not opted in" data-id="element-31" data-mp="e5hhwmtt40qq" />
        </div>

        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-33" data-mp="3coogakn74z9g">
          <div className="relative max-w-xs flex-1" data-id="element-34" data-mp="3hm6mi39tx0dr">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-35" data-mp="3mkrp29cjzw5d" />
            <input type="text" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search loan officers…" className="w-full rounded-md border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-rcm-500 focus:outline-none focus:ring-1 focus:ring-rcm-500" data-id="element-36" data-mp="3qnea9repyitl" />
          </div>
          <label className="flex cursor-pointer select-none items-center gap-2 text-sm font-semibold text-slate-600" data-id="element-37" data-mp="21nqvcekvsbee">
            <input type="checkbox" checked={showNotReady} onChange={event => setShowNotReady(event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-rcm-500 focus:ring-rcm-500" data-id="element-38" data-mp="1wwh8flqpvm67" />
            Show not-ready LOs
          </label>
        </div>

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm" data-id="element-39" data-mp="2kljvxa81898m">
          <div className="overflow-x-auto" data-id="element-40" data-mp="2foju5bi7tsnb">
            <table className="w-full border-collapse text-left" data-id="element-41" data-mp="2flnv1jumsxxa">
              <thead data-id="element-42" data-mp="q93wotdnlvd9">
                <tr className="border-b border-slate-200 bg-slate-50" data-id="element-43" data-mp="qaogpp2cdvwv">
                  <th className="sticky left-0 z-20 bg-slate-50 px-4 py-3.5 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500" data-id="element-44" data-mp="lask4avz56rc">Loan Officer</th>
                  <th className="px-4 py-3.5 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500" data-id="element-45" data-mp="31vnvuk0thtox">Weekly time blocks</th>
                  <th className="px-4 py-3.5 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500" data-id="element-46" data-mp="2n68719kujybv">Ready</th>
                  <th className="px-4 py-3.5 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500" data-id="element-47" data-mp="2s49rnyxx85pg">Licensed</th>
                  <th className="px-4 py-3.5 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500" data-id="element-48" data-mp="2e8pfdmcp6lr0">Last Assigned</th>
                  <th className="px-4 py-3.5 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500" data-id="element-49" data-mp="248oz69a4klr1">Lead Source</th>
                </tr>
              </thead>
              <tbody data-id="element-50" data-mp="29932je0s77iq">
                {sorted.map(officer => <LoanOfficerRow key={officer.id} officer={officer} sources={LEAD_SOURCES} isNextUp={officer.id === nextUpId} now={now} onTimeBlocksChange={timeBlocks => updateTimeBlocks(officer.id, timeBlocks)} onToggleReady={() => toggleReady(officer.id)} onAddSource={sourceId => addSource(officer.id, sourceId)} onAdjustCap={(sourceId, delta) => adjustCap(officer.id, sourceId, delta)} onOpenLicenses={() => setLicensesOfficerId(officer.id)} data-id="element-51" data-mp="2hbrxdkoq1rt" />)}
              </tbody>
            </table>
          </div>
          {sorted.length === 0 && <div className="flex flex-col items-center justify-center gap-2 py-16 text-center" data-id="element-52" data-mp="3gp0jfc5b4ykp">
              <UsersIcon className="h-8 w-8 text-slate-300" data-id="element-53" data-mp="3lntvd04y4hir" />
              <p className="text-sm font-semibold text-slate-500" data-id="element-54" data-mp="25awqm9aghgh8">No loan officers to show</p>
              <p className="text-xs text-slate-400" data-id="element-55" data-mp="2a9q6u9h4nll3">{showNotReady ? 'Try adjusting your search.' : 'No LOs are in a ready state yet today.'}</p>
            </div>}
        </div>

        <p className="mt-4 flex items-center gap-1.5 text-xs text-slate-400" data-id="element-56" data-mp="22s1qu2j78zsb">
          <ChevronDownIcon className="h-3.5 w-3.5 text-rcm-500" data-id="element-57" data-mp="1xqujg4kyy2m6" />
          Rows follow the round-robin tiebreaker — least-recently-assigned LOs appear first.
        </p>
      </main>

      {licensesOfficer && <StateLicensesModal officer={licensesOfficer} onClose={() => setLicensesOfficerId(null)} data-id="element-58" data-mp="26slkux8h7na8" />}
    </div>;
}
function syncOfficerReadiness(officers: LoanOfficer[], now: Date): LoanOfficer[] {
  return officers.map(officer => ({
    ...officer,
    ready: officer.manualReady ?? isScheduledNow(officer.timeBlocks, now)
  }));
}